import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MailCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/authService';

const POLL_INTERVAL_MS = 10_000;

interface PreviewVerifyEmailCardProps {
  /** Called once `email_verified_at` flips to a non-null value. */
  onVerified: () => void;
}

/**
 * F026 §9 — gating card shown in wizard step 3 when the owner hasn't verified
 * their email. Stays in the standard Resend monochrome palette (NO IG mimic).
 *
 * Unlock without a hard navigation: poll `/auth/me` every 10s and also re-check
 * on window `focus` (covers "verified in another tab → come back"). When the
 * server reports a non-null `email_verified_at`, refresh the global auth state
 * and notify the parent to swap in the chat UI.
 */
export function PreviewVerifyEmailCard({ onVerified }: PreviewVerifyEmailCardProps) {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkVerified = useCallback(async () => {
    try {
      const fresh = await authService.getMe();
      if (fresh.email_verified_at !== null) {
        await refreshUser();
        onVerified();
      }
    } catch {
      // Transient poll failure — keep the card mounted and try again next tick.
    }
  }, [onVerified, refreshUser]);

  useEffect(() => {
    const id = setInterval(checkVerified, POLL_INTERVAL_MS);
    window.addEventListener('focus', checkVerified);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', checkVerified);
    };
  }, [checkVerified]);

  async function handleResend() {
    if (!user) return;
    setResending(true);
    setError(null);
    try {
      await authService.resendVerification(user.email);
      setResent(true);
    } catch {
      setError(t('onboarding.preview.resend_error'));
    } finally {
      setResending(false);
    }
  }

  return (
    <Card data-testid="preview-verify-email-card" className="mx-auto max-w-md">
      <CardHeader className="items-center text-center">
        <MailCheck className="h-9 w-9 text-muted-foreground" aria-hidden="true" />
        <CardTitle className="text-base">
          {t('onboarding.preview.error.email_not_verified')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-center">
        <p className="text-sm text-muted-foreground">{t('onboarding.preview.verify_hint')}</p>
        <Button onClick={handleResend} disabled={resending} variant="outline">
          {t('onboarding.preview.resend_email')}
        </Button>
        {resent && (
          <p className="text-sm text-success" role="status">
            {t('onboarding.preview.resend_sent')}
          </p>
        )}
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
